import { capitalize, get, size } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import FAVRCreatorAPI from '../../api/favr-creator';
import { CustomTable } from '../../components';
import CompanyLookupSelect from '../../components/CompanyLookupSelect';
import PageBreadcrumbs from '../../components/PageBreadcrumbs';
import PageFiltersRenderer from '../../components/shared-ui/PageFiltersRenderer';
import PageContainer from '../../containers/PageContainer';
import withPageState from '../../hocs/withPageState';
import useLocationSearchQueryParser from '../../hooks/useLocationSearchQueryParser';
import useTablePagination from '../../hooks/useTablePagination';
import { formatDistanceToLocale, formatNumberWithCurrency } from '../../utils/numbers';
import { replaceCurrentPageSearchQueryParams } from '../../utils/queryParams';
import { sortColumnByNumber, sortColumnByStringField } from '../../utils/tables';

const COMMON_RESPONSE_FILTER_SETTINGS = {
  xs: 24,
  md: 12,
  lg: 6,
};

const FAVRRatesView = props => {
  const { t, location, history } = props;

  const initialFilters = useLocationSearchQueryParser(location, {
    companyId: null,
    searchTerm: '',
  });
  const [tableFilters, setTableFilters] = useState(initialFilters);

  const handleFiltersChange = useCallback(
    filters => {
      setTableFilters(filters);
      replaceCurrentPageSearchQueryParams(history, filters);
    },
    [history],
  );

  const {
    paginationConfig,
    handlePageChange,
    handlePageSizeChange,
    handleTotalUpdate,
  } = useTablePagination();

  const selectNormalizedFavrRatesItems = useCallback(data => {
    const normalizeRates = (items = []) => {
      return items.map(item => {
        let date = '-';

        if (item?.rates?.date) {
          const year = item.rates.date.substring(0, 4);
          const month = item.rates.date.substring(4, 6);
          const day = item.rates.date.substring(6, 8);
          date = [year, month, day].join('-');
        }

        return {
          ...item,
          date,
        };
      });
    };

    return {
      items: normalizeRates(data.items),
    };
  }, []);

  const favrRatesQuery = useQuery({
    queryKey: ['fetchFavrRates', ...Object.values(tableFilters)],
    select: selectNormalizedFavrRatesItems,
    onSuccess: data => {
      handlePageChange(1);
      handleTotalUpdate(size(data.items || []));
    },
    queryFn: () =>
      new FAVRCreatorAPI().fetchFavrRates(
        tableFilters,
        paginationConfig.pageSize,
        paginationConfig.current,
      ),
  });

  const COLUMNS = useMemo(
    () => [
      {
        dataIndex: 'uid',
        title: t('driverEmail'),
        ...sortColumnByStringField('uid'),
      },
      {
        dataIndex: ['address', 'state'],
        title: t('state'),
        render: state => state || '-',
        ...sortColumnByStringField('address.state'),
      },
      {
        dataIndex: ['address', 'zip'],
        title: t('zipCode'),
        ...sortColumnByNumber('address.zip'),
      },
      {
        dataIndex: 'make',
        title: t('make'),
        render: make => capitalize(make),
        ...sortColumnByStringField('make'),
      },
      {
        dataIndex: 'model',
        title: t('model'),
        render: model => capitalize(model),
        ...sortColumnByStringField('model'),
      },
      {
        align: 'right',
        dataIndex: 'year',
        title: t('year'),
        ...sortColumnByNumber('year'),
      },
      {
        align: 'right',
        dataIndex: 'retention_years',
        title: t('retentionYears'),
        ...sortColumnByNumber('retention_years'),
      },
      {
        align: 'right',
        dataIndex: 'retention_miles',
        title: t('retentionMiles'),
        render: miles => formatDistanceToLocale(miles) || '-',
        ...sortColumnByNumber('retention_miles'),
      },
      {
        dataIndex: 'date',
        title: t('rateDate'),
        ...sortColumnByStringField('date'),
      },
      {
        align: 'right',
        dataIndex: ['rates', 'fixed_rate'],
        title: t('fixedRate'),
        render: rate => formatNumberWithCurrency(rate, 'USD') || '-',
        ...sortColumnByNumber('rates.fixed_rate'),
      },
      {
        align: 'right',
        dataIndex: ['rates', 'variable_rate'],
        title: t('variableRate'),
        render: rate => formatNumberWithCurrency(rate, 'USD') || '-',
        ...sortColumnByNumber('rates.variable_rate'),
      },
    ],
    [t],
  );

  return (
    <PageContainer title={<PageBreadcrumbs items={[{ label: t('favrRates') }]} />}>
      <PageFiltersRenderer
        t={t}
        loading={favrRatesQuery.isLoading}
        onFiltersChange={handleFiltersChange}
        search={{
          placeholder: t('searchByEmail'),
          defaultValue: initialFilters.searchTerm,
        }}
        filters={[
          {
            componentType: 'custom',
            label: t('Company'),
            name: 'companyId',
            responsive: COMMON_RESPONSE_FILTER_SETTINGS,
            defaultValue: initialFilters.companyId || null,
            render: ({ updateFilterValues, disabled, loading }) => (
              <CompanyLookupSelect
                t={t}
                showAllOption
                disabled={disabled || loading}
                value={tableFilters?.companyId}
                defaultValue={initialFilters.companyId || null}
                onChange={companyId => updateFilterValues({ companyId })}
              />
            ),
          },
        ]}
      />

      <CustomTable
        rowKey={row =>
          JSON.stringify({ uid: row.uid, zip_model_yr_retention: row.zip_model_yr_retention })
        }
        pagination={{
          pageSize: paginationConfig.pageSize,
          current: paginationConfig.current,
          onShowSizeChange: handlePageSizeChange,
        }}
        onChange={({ current }) => {
          handlePageChange(current);
        }}
        scroll={{ x: 1400 }}
        columns={COLUMNS}
        dataSource={get(favrRatesQuery.data, 'items', [])}
        showSearchInput={false}
        loading={favrRatesQuery.isLoading}
      />
    </PageContainer>
  );
};

export default withPageState(FAVRRatesView, true);
